import { useState, useEffect, useCallback } from 'react';
import { Bot, RefreshCw, ShieldCheck, AlertCircle } from 'lucide-react';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { cn } from '../../lib/utils.js';

function formatGermanDate(isoString) {
  if (!isoString) return 'Keine Aufzeichnungen';
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return String(isoString);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Europe/Berlin',
    }) + ' Uhr';
  } catch {
    return String(isoString);
  }
}

export function AlwisQualityCard({
  initialInsights = null,
  initialStatus = null,
  initialError = null,
  className = '',
}) {
  const [status, setStatus] = useState(() => {
    if (initialStatus) return initialStatus;
    if (initialInsights) return 'success';
    if (initialError) return 'error';
    return 'loading';
  });

  const [insights, setInsights] = useState(initialInsights);
  const [errorMsg, setErrorMsg] = useState(initialError);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchInsights = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setStatus('loading');
    }
    setErrorMsg(null);

    try {
      const res = await fetch('/api/portal/alwis-insights', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}: Telemetrie nicht verfügbar`);
      }

      const data = await res.json();
      if (!data || data.ok === false) {
        throw new Error(data?.error || 'Ungültige Telemetriedaten empfangen');
      }

      setInsights(data.insights || data);
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.message || 'Verbindung zur D1-Telemetrie fehlgeschlagen');
      setStatus('error');
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (initialInsights || initialStatus || initialError) {
      return;
    }
    fetchInsights();
  }, [fetchInsights, initialInsights, initialStatus, initialError]);

  return (
    <div
      data-testid="alwis-quality-card"
      className={cn(
        'rounded-2xl border border-[var(--border,#e2e8f0)] bg-white p-6 shadow-xs space-y-5 transition-all',
        className
      )}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-[var(--border,#e2e8f0)]">
        <div className="flex items-center gap-3">
          <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-[var(--primary,#e3000b)] border border-red-100">
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-sm font-bold text-[var(--foreground,#181716)]">
                ALWIS Chatqualität &amp; D1-Telemetrie
              </h3>
              <Badge
                variant="outline"
                className={cn(
                  'text-[11px] font-semibold py-0.5 px-2 font-mono',
                  status === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : status === 'loading'
                    ? 'bg-slate-50 text-slate-700 border-slate-200'
                    : 'bg-red-50 text-red-800 border-red-200'
                )}
              >
                {status === 'success'
                  ? 'D1 Live-Telemetrie'
                  : status === 'loading'
                  ? 'Lade Daten...'
                  : 'Nicht verbunden'}
              </Badge>
            </div>
            <p className="text-xs text-[var(--secondary,#475569)] mt-0.5">
              Aggregierte Metriken aus <code className="font-mono font-bold text-slate-700">chat_turn_records</code> (asl-chat-logs)
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="alwis-quality-refresh"
            onClick={() => fetchInsights(true)}
            disabled={status === 'loading' || isRefreshing}
            className="h-8 px-2.5 text-xs text-[var(--secondary)] hover:text-[var(--primary)] gap-1.5 cursor-pointer"
            aria-label="Telemetrie aktualisieren"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', (status === 'loading' || isRefreshing) && 'animate-spin text-[var(--primary)]')} />
            <span>Aktualisieren</span>
          </Button>
        </div>
      </div>

      {/* State 1: Loading */}
      {status === 'loading' && (
        <div
          data-testid="alwis-quality-loading"
          className="flex flex-col items-center justify-center py-10 space-y-3 text-slate-500"
        >
          <RefreshCw className="h-6 w-6 animate-spin text-[var(--primary,#e3000b)]" />
          <p className="text-xs font-medium">Lade aggregierte Chatqualität aus D1...</p>
        </div>
      )}

      {/* State 2: Error / Not connected */}
      {status === 'error' && (
        <div
          data-testid="alwis-quality-error"
          className="rounded-xl border border-red-200 bg-red-50/80 p-4 space-y-3"
        >
          <div className="flex items-start gap-2.5">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="space-y-1 text-xs">
              <h4 className="font-bold text-red-900">
                Telemetrie-Verbindung nicht verfügbar
              </h4>
              <p className="text-red-700 leading-relaxed">
                {errorMsg || 'Die aggregierten ALWIS-Chatqualitätsdaten konnten nicht aus der Datenbank abgerufen werden.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fetchInsights(false)}
              className="h-7 text-xs font-bold border-red-300 text-red-900 bg-white hover:bg-red-50"
            >
              Erneut versuchen
            </Button>
          </div>
        </div>
      )}

      {/* State 3: Live D1 with Aggregates */}
      {status === 'success' && insights && (
        <div data-testid="alwis-quality-live" className="space-y-5 animate-fadeIn">
          {/* Main KPI Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5">
              <span className="text-[11px] text-slate-500 font-medium block">Echte gespeicherte Turns</span>
              <p
                data-testid="alwis-stat-turns"
                className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mt-1"
              >
                {insights.total_turns}
              </p>
              <span className="text-[10px] text-slate-500 font-mono mt-0.5 block">active · Evals ausgeschlossen</span>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5">
              <span className="text-[11px] text-slate-500 font-medium block">Gespräche (Sessions)</span>
              <p
                data-testid="alwis-stat-sessions"
                className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mt-1"
              >
                {insights.distinct_sessions}
              </p>
              <span className="text-[10px] text-slate-500 font-mono mt-0.5 block">distinct session_id</span>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5">
              <span className="text-[11px] text-slate-500 font-medium block">Grounded-Status</span>
              <p
                data-testid="alwis-stat-grounded"
                className="font-display text-2xl font-extrabold text-emerald-800 tabular-nums mt-1"
              >
                {insights.grounded}
              </p>
              <span
                data-testid="alwis-stat-grounded-rate"
                className="text-[11px] font-bold text-emerald-800 tabular-nums mt-0.5 block"
              >
                {insights.total_turns > 0 ? `${(insights.grounded_rate * 100).toFixed(1)}%` : '0.0%'} Quote
              </span>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3.5">
              <span className="text-[11px] text-slate-500 font-medium block">Evidenzlücken</span>
              <p
                data-testid="alwis-stat-insufficient"
                className="font-display text-2xl font-extrabold text-amber-800 tabular-nums mt-1"
              >
                {insights.insufficient_evidence}
              </p>
              <span className="text-[10px] text-amber-800 font-medium mt-0.5 block">Abstinenz-Vertrag</span>
            </div>
          </div>

          {/* Secondary Stats Strip */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-xs">
              <span className="text-slate-600 font-medium">Eskalationen:</span>
              <span
                data-testid="alwis-stat-escalated"
                className="font-mono tabular-nums font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded"
              >
                {insights.escalated}
              </span>
            </div>

            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-xs">
              <span className="text-slate-600 font-medium">Backend-Fehler:</span>
              <span
                data-testid="alwis-stat-backend-errors"
                className={cn(
                  'font-mono tabular-nums font-bold px-2 py-0.5 rounded',
                  insights.backend_errors > 0 ? 'text-red-800 bg-red-50' : 'text-slate-900 bg-slate-100'
                )}
              >
                {insights.backend_errors}
              </span>
            </div>

            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-xs">
              <span className="text-slate-600 font-medium">Letzter Turn:</span>
              <span
                data-testid="alwis-stat-last-turn"
                className="font-mono tabular-nums text-[11px] font-bold text-slate-800 truncate max-w-[160px]"
                title={insights.last_turn || 'Keine Aufzeichnungen'}
              >
                {formatGermanDate(insights.last_turn)}
              </span>
            </div>
          </div>

          {/* Honest Governance & Privacy Architecture Notice */}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 space-y-1.5 text-xs text-slate-600">
            <div className="flex items-center gap-2 font-bold text-slate-800">
              <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>Datenschutz- &amp; Wahrheitsprotokoll</span>
            </div>
            <p className="text-[11px] leading-relaxed">
              Live D1-Telemetrie (<code className="font-mono">asl-chat-logs</code>) ohne PII oder Rohdaten-Exposition. Grounded ist der protokollierte Worker-Status, keine fachliche Validierung. Keine automatisierten Selbstlern- oder Modelltrainingsprozesse; Nachweise basieren ausschließlich auf dem autorisierten Dokumentenkorpus.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
